import { isValid, parseISO } from 'date-fns';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import {
    type Dispatch,
    type ReactNode,
    type SetStateAction,
    useCallback,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { CardTransition } from '@apps/web/features/phoebe_chat_v3/ui/primitives/card_transition';
import { CompactReceipt } from '@apps/web/features/phoebe_chat_v3/ui/primitives/compact_receipt';
import {
    EntityPill,
    ResolvedReferenceText,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';
import { formatDateAndTimeWithTz } from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';

const LOADING_ROW_COUNT = 3;
// The agent keeps id columns in its query results so rows can be acted on
// later; readers never need to see them.
const ID_COLUMN = /(^|_)id$/iu;

function visibleColumnsOf(
    columns: ReadonlyArray<string>,
): ReadonlyArray<string> {
    const visible = columns.filter((column) => !ID_COLUMN.test(column));
    return visible.length === 0 ? columns : visible;
}

// Column names arrive as data-model identifiers; readers should see plain
// labels, not schema jargon.
const COLUMN_LABEL_OVERRIDES: Readonly<Record<string, string>> = {
    display_name: 'Name',
};

function columnLabel(column: string): string {
    const override =
        COLUMN_LABEL_OVERRIDES[column.toLowerCase().replace(/ /gu, '_')];
    if (override !== undefined) {
        return override;
    }
    const words = column.replace(/_/gu, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
}

// Client cells become profile pills whenever an id travels with the name —
// either a {id, name} record or a name column beside a hidden client_id.
function clientCellPill(
    column: string,
    row: Readonly<Record<string, unknown>>,
): Readonly<{ readonly id: string; readonly name: string }> | null {
    const key = column.toLowerCase().replace(/ /gu, '_');
    if (key !== 'client' && key !== 'client_name') {
        return null;
    }
    const value = row[column];
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        const record = value as {
            readonly id?: unknown;
            readonly name?: unknown;
        };
        if (
            typeof record.id === 'string' &&
            record.id !== '' &&
            typeof record.name === 'string' &&
            record.name.trim() !== ''
        ) {
            return { id: record.id, name: record.name };
        }
        return null;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const idKey = Object.keys(row).find(
            (candidate) =>
                candidate.toLowerCase().replace(/ /gu, '_') === 'client_id',
        );
        const id = idKey === undefined ? undefined : row[idKey];
        if (typeof id === 'string' && id !== '') {
            return { id, name: value };
        }
    }
    return null;
}

function cellContent(
    column: string,
    row: Readonly<Record<string, unknown>>,
    organizationTimeZone: string,
): ReactNode {
    const pill = clientCellPill(column, row);
    if (pill !== null) {
        return <EntityPill type="client" id={pill.id} label={pill.name} />;
    }
    return (
        <ResolvedReferenceText
            text={formatCell(column, row[column], organizationTimeZone)}
        />
    );
}

export type GeneratedTableAction = Readonly<{
    readonly label: string;
    readonly prompt: string;
    /** The most rows the action accepts; above it the button waits. */
    readonly maxRows?: number;
    /** What to say in place of the count while over `maxRows`. */
    readonly maxRowsMessage?: string;
}>;

export type GeneratedTableActionReceipt = Readonly<{
    readonly status: string;
    readonly label: string;
    readonly href: string;
}>;

export type GeneratedTableSelection = Readonly<{
    /** Column holding each row's id; the reader never sees it. */
    readonly idColumn: string;
    readonly actions: ReadonlyArray<GeneratedTableAction>;
    readonly selectedIds?: ReadonlySet<string>;
    readonly onSelectedIdsChange?: Dispatch<
        SetStateAction<ReadonlySet<string>>
    >;
    readonly completedAction?: GeneratedTableActionReceipt;
    readonly disabled?: boolean;
    readonly statusMessage?: string;
    readonly onAction: (
        action: GeneratedTableAction,
        selectedIds: ReadonlyArray<string>,
        completeAction: (receipt?: GeneratedTableActionReceipt) => void,
    ) => void | Promise<void>;
}>;

/** Clicking a row opens its record directly — reviewing a row's details
 * never needs a round-trip through the agent. */
export type GeneratedTableRowOpen = Readonly<{
    /** Column holding each row's id; the reader never sees it. */
    readonly idColumn: string;
    readonly onOpen: (id: string) => void;
}>;

export type GeneratedTableProps = Readonly<{
    readonly title: string;
    readonly organizationTimeZone: string;
    readonly columns: ReadonlyArray<string>;
    readonly columnsTruncated: boolean;
    readonly rows: ReadonlyArray<Readonly<Record<string, unknown>>>;
    readonly rowCount: number;
    readonly pageOffset: number;
    /** Rows per page; short last pages pad to it so paging keeps one height. */
    readonly pageSizeRows?: number;
    readonly isLoading: boolean;
    readonly isLoadingMore: boolean;
    readonly loadError: boolean;
    readonly pageLoadError?: boolean;
    readonly hasPrevious: boolean;
    readonly hasMore: boolean;
    readonly onPrevious: () => void;
    readonly onNext: () => void;
    readonly selection?: GeneratedTableSelection;
    readonly rowOpen?: GeneratedTableRowOpen;
}>;

function rowId(
    row: Readonly<Record<string, unknown>>,
    idColumn: string,
): string | null {
    const value = row[idColumn];
    return typeof value === 'string' && value !== '' ? value : null;
}

// Columns keep their natural widths and overflow sideways when the card is
// narrower, but macOS overlay scrollbars leave no visible hint that more
// columns exist — edge fades carry that signal instead.
function TableScrollFades({
    children,
}: Readonly<{ readonly children: ReactNode }>): ReactNode {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [fades, setFades] = useState({ left: false, right: false });
    const updateFades = useCallback(() => {
        const element = scrollRef.current;
        if (element === null) {
            return;
        }
        const maxScrollLeft = element.scrollWidth - element.clientWidth;
        // A 1px tolerance absorbs sub-pixel scroll positions at the edges.
        const left = element.scrollLeft > 1;
        const right = element.scrollLeft < maxScrollLeft - 1;
        setFades((current) =>
            current.left === left && current.right === right
                ? current
                : { left, right },
        );
    }, []);
    useEffect(() => {
        const element = scrollRef.current;
        if (element === null) {
            return;
        }
        updateFades();
        // Content width changes with paging and loading states, not only
        // with the viewport, so observe the children too.
        const observer = new ResizeObserver(updateFades);
        observer.observe(element);
        for (const child of element.children) {
            observer.observe(child);
        }
        return () => observer.disconnect();
    }, [updateFades]);
    return (
        <div className="relative border-t">
            <div
                className="overflow-x-auto"
                onScroll={updateFades}
                ref={scrollRef}
            >
                {children}
            </div>
            {/* z-20 keeps the fades above the sticky header (z-10). */}
            {fades.left ? (
                <div
                    aria-hidden="true"
                    className="from-card pointer-events-none absolute inset-y-0 left-0 z-20 w-6 bg-gradient-to-r to-transparent"
                />
            ) : null}
            {fades.right ? (
                <div
                    aria-hidden="true"
                    className="from-card pointer-events-none absolute inset-y-0 right-0 z-20 w-6 bg-gradient-to-l to-transparent"
                />
            ) : null}
        </div>
    );
}

// A long value, such as a message thread, would otherwise stretch its row
// to dozens of lines. The chevron appears only when the clamp hides text,
// and making room for it can only keep that text overflowing.
function ClampedCellText({
    children,
}: Readonly<{ readonly children: ReactNode }>): ReactNode {
    const textRef = useRef<HTMLDivElement | null>(null);
    const [overflows, setOverflows] = useState(false);
    const [expanded, setExpanded] = useState(false);
    useEffect(() => {
        const element = textRef.current;
        // Expanded text has no clamp to measure, and the chevron has to stay
        // so the reader can collapse it again.
        if (element === null || expanded) {
            return;
        }
        const measure = (): void =>
            setOverflows(element.scrollHeight > element.clientHeight);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [expanded]);
    return (
        <div className="flex items-end gap-1">
            <div
                className={cn('min-w-0 flex-1', !expanded && 'line-clamp-3')}
                ref={textRef}
            >
                {children}
            </div>
            {overflows ? (
                <button
                    aria-expanded={expanded}
                    aria-label={expanded ? 'Show less' : 'Show more'}
                    className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 flex h-[1lh] shrink-0 items-center rounded-sm outline-none focus-visible:ring-[3px]"
                    onClick={(event) => {
                        // Rows can open their record on click; the chevron
                        // only toggles its cell.
                        event.stopPropagation();
                        setExpanded(!expanded);
                    }}
                    type="button"
                >
                    <ChevronDown
                        aria-hidden="true"
                        className={cn(
                            'size-[1em] transition-transform duration-150 motion-reduce:transition-none',
                            expanded && 'rotate-180',
                        )}
                    />
                </button>
            ) : null}
        </div>
    );
}

export function GeneratedTable({
    title,
    organizationTimeZone,
    columns,
    columnsTruncated,
    rows,
    rowCount,
    pageOffset,
    pageSizeRows,
    isLoading,
    isLoadingMore,
    loadError,
    pageLoadError = false,
    hasPrevious,
    hasMore,
    onPrevious,
    onNext,
    selection,
    rowOpen,
}: GeneratedTableProps): ReactNode {
    const titleId = useId();
    const visibleColumns = visibleColumnsOf(columns);
    // Selection is keyed by row id so it survives paging.
    const [localSelectedIds, setLocalSelectedIds] = useState<
        ReadonlySet<string>
    >(() => new Set());
    const selectedIds = selection?.selectedIds ?? localSelectedIds;
    const setSelectedIds =
        selection?.onSelectedIdsChange ?? setLocalSelectedIds;
    const [busy, setBusy] = useState(false);
    const [completedAction, setCompletedAction] =
        useState<GeneratedTableActionReceipt>();
    const receipt = completedAction ?? selection?.completedAction;
    const overCap = (action: GeneratedTableAction): boolean =>
        action.maxRows !== undefined && selectedIds.size > action.maxRows;
    const capMessage = selection?.actions.find(overCap)?.maxRowsMessage;
    function runAction(action: GeneratedTableAction): void {
        if (
            selection === undefined ||
            selection.disabled ||
            busy ||
            selectedIds.size === 0
        ) {
            return;
        }
        const actionSelectedIds = Array.from(selectedIds);
        const result = selection.onAction(
            action,
            actionSelectedIds,
            (receipt) => {
                if (receipt !== undefined) {
                    setCompletedAction(receipt);
                }
                // Keep rows selected while this action was in flight.
                setSelectedIds((current) => {
                    const next = new Set(current);
                    for (const id of actionSelectedIds) {
                        next.delete(id);
                    }
                    return next;
                });
            },
        );
        if (result instanceof Promise) {
            setBusy(true);
            void result.finally(() => setBusy(false));
        }
    }
    const pageIds =
        selection === undefined
            ? []
            : rows.flatMap((row) => {
                  const id = rowId(row, selection.idColumn);
                  return id === null ? [] : [id];
              });
    const allOnPageSelected =
        pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
    function toggleRow(id: string, checked: boolean): void {
        setSelectedIds((current) => {
            const next = new Set(current);
            if (checked) {
                next.add(id);
            } else {
                next.delete(id);
            }
            return next;
        });
    }
    function togglePage(checked: boolean): void {
        setSelectedIds((current) => {
            const next = new Set(current);
            for (const id of pageIds) {
                if (checked) {
                    next.add(id);
                } else {
                    next.delete(id);
                }
            }
            return next;
        });
    }
    const firstVisibleRow = rows.length === 0 ? 0 : pageOffset + 1;
    const lastVisibleRow = Math.min(pageOffset + rows.length, rowCount);
    const showPagination = hasPrevious || hasMore;
    // A short last page pads to the page size so paging never resizes the
    // card.
    const fillerRowCount =
        showPagination && !isLoading && pageSizeRows !== undefined
            ? Math.max(0, pageSizeRows - rows.length)
            : 0;

    if (receipt !== undefined) {
        return (
            <CardTransition transitionKey="receipt">
                <CompactReceipt
                    surface="table"
                    title={receipt.status}
                    status="succeeded"
                    action={
                        <a
                            href={receipt.href}
                            className="text-agent-action hover:text-agent-action-strong text-v3-chat rounded-md font-semibold underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 dark:text-violet-300 dark:hover:text-violet-200"
                        >
                            {receipt.label}
                        </a>
                    }
                />
            </CardTransition>
        );
    }

    return (
        <CardTransition transitionKey="table">
            <section
                aria-labelledby={titleId}
                aria-busy={isLoading || isLoadingMore}
                className="bg-card @container/generated-table w-full min-w-0 overflow-hidden rounded-lg border"
            >
                <header
                    aria-labelledby={titleId}
                    className="bg-muted relative flex min-h-10 min-w-0 items-start justify-center gap-3 px-3 py-2.5"
                    role="group"
                >
                    <div className="min-w-0 flex-1 text-center">
                        <h3
                            className="text-v3-chat text-center font-bold [overflow-wrap:anywhere]"
                            id={titleId}
                        >
                            <ResolvedReferenceText text={title} />
                        </h3>
                        {columnsTruncated ? (
                            <p className="text-muted-foreground text-v3-chat-meta">
                                Some columns could not be displayed.
                            </p>
                        ) : null}
                    </div>
                </header>
                {loadError ? (
                    <p className="text-muted-foreground text-v3-chat border-t px-3 py-4">
                        This table could not be loaded.
                    </p>
                ) : (
                    <TableScrollFades>
                        <Table
                            containerClassName="overflow-x-visible"
                            className="text-v3-chat min-w-max"
                        >
                            <TableHeader className="sticky top-0 z-10">
                                <TableRow className="hover:bg-transparent">
                                    {selection !== undefined ? (
                                        <TableHead className="bg-muted/50 h-8 w-8 px-3">
                                            <Checkbox
                                                aria-label="Select all rows"
                                                checked={allOnPageSelected}
                                                disabled={
                                                    pageIds.length === 0 ||
                                                    selection.disabled
                                                }
                                                onCheckedChange={(checked) =>
                                                    togglePage(checked === true)
                                                }
                                            />
                                        </TableHead>
                                    ) : null}
                                    {visibleColumns.map((column) => (
                                        <TableHead
                                            className="bg-muted/50 text-v3-chat-meta h-8 px-3 font-bold"
                                            key={column}
                                            scope="col"
                                        >
                                            {columnLabel(column)}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading
                                    ? Array.from({
                                          length: LOADING_ROW_COUNT,
                                      }).map((_, rowIndex) => (
                                          <TableRow
                                              className="even:bg-muted/30"
                                              key={rowIndex}
                                          >
                                              {visibleColumns.map((column) => (
                                                  <TableCell
                                                      className="h-9 px-3"
                                                      key={column}
                                                  >
                                                      <Skeleton className="h-3 w-20" />
                                                  </TableCell>
                                              ))}
                                          </TableRow>
                                      ))
                                    : rows.map((row, rowIndex) => {
                                          const openId =
                                              rowOpen === undefined
                                                  ? null
                                                  : rowId(
                                                        row,
                                                        rowOpen.idColumn,
                                                    );
                                          const open =
                                              openId === null ||
                                              rowOpen === undefined
                                                  ? undefined
                                                  : () =>
                                                        rowOpen.onOpen(openId);
                                          return (
                                              <TableRow
                                                  className={cn(
                                                      'even:bg-muted/30',
                                                      open !== undefined &&
                                                          'focus-visible:outline-agent-action cursor-pointer outline-none focus-visible:outline-2 focus-visible:-outline-offset-2',
                                                  )}
                                                  key={pageOffset + rowIndex}
                                                  onClick={open}
                                                  onKeyDown={
                                                      open === undefined
                                                          ? undefined
                                                          : (event) => {
                                                                // Keys from the
                                                                // checkbox stay its
                                                                // own; only keys on
                                                                // the row itself
                                                                // open the record.
                                                                if (
                                                                    event.target !==
                                                                    event.currentTarget
                                                                ) {
                                                                    return;
                                                                }
                                                                if (
                                                                    event.key ===
                                                                        'Enter' ||
                                                                    event.key ===
                                                                        ' '
                                                                ) {
                                                                    event.preventDefault();
                                                                    open();
                                                                }
                                                            }
                                                  }
                                                  tabIndex={
                                                      open === undefined
                                                          ? undefined
                                                          : 0
                                                  }
                                              >
                                                  {selection !== undefined ? (
                                                      // Ticking a row must not also
                                                      // open it, so clicks in the
                                                      // checkbox cell stay there.
                                                      <TableCell
                                                          className="w-8 px-3 py-2 align-top"
                                                          onClick={(event) =>
                                                              event.stopPropagation()
                                                          }
                                                      >
                                                          {(() => {
                                                              const id = rowId(
                                                                  row,
                                                                  selection.idColumn,
                                                              );
                                                              return id ===
                                                                  null ? null : (
                                                                  <Checkbox
                                                                      aria-label={`Select row ${pageOffset + rowIndex + 1}`}
                                                                      disabled={
                                                                          selection.disabled
                                                                      }
                                                                      checked={selectedIds.has(
                                                                          id,
                                                                      )}
                                                                      onCheckedChange={(
                                                                          checked,
                                                                      ) =>
                                                                          toggleRow(
                                                                              id,
                                                                              checked ===
                                                                                  true,
                                                                          )
                                                                      }
                                                                  />
                                                              );
                                                          })()}
                                                      </TableCell>
                                                  ) : null}
                                                  {visibleColumns.map(
                                                      (column) => (
                                                          <TableCell
                                                              className="max-w-64 px-3 py-2 align-top break-words whitespace-pre-wrap"
                                                              key={column}
                                                          >
                                                              <ClampedCellText>
                                                                  {cellContent(
                                                                      column,
                                                                      row,
                                                                      organizationTimeZone,
                                                                  )}
                                                              </ClampedCellText>
                                                          </TableCell>
                                                      ),
                                                  )}
                                              </TableRow>
                                          );
                                      })}
                                {Array.from({ length: fillerRowCount }).map(
                                    (_, fillerIndex) => (
                                        <TableRow
                                            aria-hidden={true}
                                            className="even:bg-muted/30"
                                            key={`filler-${fillerIndex}`}
                                        >
                                            {selection !== undefined ? (
                                                <TableCell className="w-8 px-3 py-2" />
                                            ) : null}
                                            {visibleColumns.map((column) => (
                                                <TableCell
                                                    className="px-3 py-2"
                                                    key={column}
                                                >
                                                    {' '}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ),
                                )}
                            </TableBody>
                        </Table>
                    </TableScrollFades>
                )}
                {!loadError && !isLoading ? (
                    <footer className="text-muted-foreground text-v3-chat-meta flex min-h-8 flex-wrap items-center justify-between gap-2 border-t px-3 py-1">
                        <div className="flex items-center gap-2">
                            {showPagination ? (
                                <div className="flex gap-1">
                                    <Button
                                        aria-label="Previous page"
                                        className="size-6 rounded-md"
                                        disabled={!hasPrevious || isLoadingMore}
                                        onClick={onPrevious}
                                        size="icon"
                                        type="button"
                                        variant="outline"
                                    >
                                        <ChevronLeft className="size-3.5" />
                                    </Button>
                                    <Button
                                        aria-label="Next page"
                                        className="size-6 rounded-md"
                                        disabled={!hasMore || isLoadingMore}
                                        onClick={onNext}
                                        size="icon"
                                        type="button"
                                        variant="outline"
                                    >
                                        <ChevronRight className="size-3.5" />
                                    </Button>
                                </div>
                            ) : null}
                            <span aria-hidden={true} className="tabular-nums">
                                <ResolvedReferenceText
                                    text={
                                        pageLoadError
                                            ? 'Could not load the next page.'
                                            : selection?.statusMessage !==
                                                undefined
                                              ? selection.statusMessage
                                              : capMessage !== undefined
                                                ? capMessage
                                                : selection !== undefined &&
                                                    selectedIds.size > 0
                                                  ? `${selectedIds.size} selected`
                                                  : `${rowCount} ${rowCount === 1 ? 'result' : 'results'}`
                                    }
                                />
                            </span>
                        </div>
                        {/* Paging swaps rows in place, so the visible label alone
                        never changes. Announce the range that actually moved. */}
                        <span aria-live="polite" className="sr-only">
                            {pageLoadError
                                ? 'Could not load the next page.'
                                : `Results ${firstVisibleRow} to ${lastVisibleRow} of ${rowCount}`}
                        </span>
                        {selection !== undefined &&
                        selection.actions.length > 0 ? (
                            <div className="flex gap-1">
                                {selection.actions.map((action) => (
                                    <Button
                                        className="border-agent-action bg-agent-action hover:border-agent-action-strong hover:bg-agent-action-strong text-v3-chat h-8 rounded-md px-2 font-semibold text-white"
                                        disabled={
                                            selectedIds.size === 0 ||
                                            selection.disabled ||
                                            busy ||
                                            overCap(action)
                                        }
                                        key={action.label}
                                        onClick={() => runAction(action)}
                                        size="sm"
                                        type="button"
                                    >
                                        <ResolvedReferenceText
                                            text={action.label}
                                        />
                                    </Button>
                                ))}
                            </div>
                        ) : null}
                    </footer>
                ) : null}
            </section>
        </CardTransition>
    );
}

// Domain rows arrive as canonical data, not display strings: wall-clock
// times without a zone, snake_case statuses, records as {id, name}.
const WALL_CLOCK =
    /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?)?$/u;
const ZONED_DATE_TIME =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/u;
const SNAKE_CASE = /^[a-z]+(?:_[a-z]+)+$/u;
const DATE_LABEL = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
});
const DATE_TIME_LABEL = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
});

function humanize(value: string): string {
    const words = value.replace(/_/gu, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
}

function formatWallClock(value: string): string | null {
    const match = WALL_CLOCK.exec(value);
    if (match === null) {
        return null;
    }
    const [, year, month, day, hour, minute] = match;
    const date = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour ?? 0),
        Number(minute ?? 0),
    );
    return hour === undefined
        ? DATE_LABEL.format(date)
        : DATE_TIME_LABEL.format(date);
}

function formatCell(
    column: string,
    value: unknown,
    organizationTimeZone: string,
): string {
    if (value === null || value === undefined) {
        return '—';
    }
    if (typeof value === 'string') {
        if (column === 'status' || SNAKE_CASE.test(value)) {
            return humanize(value);
        }
        // Date.parse rolls impossible calendar dates into the next month.
        if (
            ZONED_DATE_TIME.test(value) &&
            isValid(parseISO(value)) &&
            !Number.isNaN(Date.parse(value))
        ) {
            return formatDateAndTimeWithTz(value, organizationTimeZone);
        }
        return formatWallClock(value) ?? value;
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    if (typeof value === 'number') {
        return String(value);
    }
    if (Array.isArray(value)) {
        return value
            .map((item) => formatCell(column, item, organizationTimeZone))
            .join(', ');
    }
    if (typeof value === 'object' && 'name' in value) {
        const name = (value as { readonly name: unknown }).name;
        if (typeof name === 'string' && name.trim() !== '') {
            return name;
        }
    }
    return JSON.stringify(value) ?? '—';
}
